// src/services/auditAPI.ts
// API service for Audit System endpoints

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';
import { 
  AuditRecord, 
  AuditQueryFilters, 
  AuditQueryResponse,
  AuditEventType 
} from '../types/audit';

// Create service-specific logger
const auditLogger = logger.createServiceLogger('AuditAPI');

class AuditAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  /**
   * Make authenticated request to audit endpoints
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const endTimer = logger.startTimer('AuditAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('AuditAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        options
      );

      logger.logAPIResponse('AuditAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const data = await response.json();

      if (!response.ok) {
        auditLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        throw new Error(data.error?.message || `HTTP ${response.status}`);
      }

      return data as T;
    } catch (error: any) {
      endTimer();
      auditLogger.error('Request failed', { endpoint, method: options.method }, error);
      throw error;
    }
  }

  /**
   * Get all audit records for tenant with optional filters
   * GET /audit
   */
  async getAuditRecords(filters?: AuditQueryFilters): Promise<AuditQueryResponse> {
    auditLogger.debug('Fetching audit records', { filters });

    const params = new URLSearchParams();
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.eventType) {
      params.append('eventType', filters.eventType);
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.lastEvaluatedKey) {
      params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
    }

    const queryString = params.toString();
    const endpoint = queryString ? `/audit?${queryString}` : '/audit';

    return this.makeRequest<AuditQueryResponse>(endpoint);
  }

  /**
   * Get audit history for a specific item
   * GET /audit/item/{itemId}
   */
  async getItemAuditHistory(
    itemId: string, 
    filters?: { startDate?: string; endDate?: string; limit?: number; lastEvaluatedKey?: string }
  ): Promise<AuditQueryResponse> {
    auditLogger.debug('Fetching item audit history', { itemId, filters });

    const params = new URLSearchParams();
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.lastEvaluatedKey) {
      params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
    }

    const queryString = params.toString();
    const endpoint = queryString 
      ? `/audit/item/${encodeURIComponent(itemId)}?${queryString}` 
      : `/audit/item/${encodeURIComponent(itemId)}`;

    return this.makeRequest<AuditQueryResponse>(endpoint);
  }

  /**
   * Get audit history for a specific user
   * GET /audit/user/{userId}
   */
  async getUserAuditHistory(
    userId: string,
    filters?: { startDate?: string; endDate?: string; limit?: number; lastEvaluatedKey?: string }
  ): Promise<AuditQueryResponse> {
    auditLogger.debug('Fetching user audit history', { userId, filters });

    const params = new URLSearchParams();
    
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.lastEvaluatedKey) {
      params.append('lastEvaluatedKey', filters.lastEvaluatedKey);
    }

    const queryString = params.toString();
    const endpoint = queryString 
      ? `/audit/user/${encodeURIComponent(userId)}?${queryString}` 
      : `/audit/user/${encodeURIComponent(userId)}`;

    return this.makeRequest<AuditQueryResponse>(endpoint);
  }
}

// Export singleton instance
export const auditAPI = new AuditAPI();

export default auditAPI;
